<template>
  <div class="calendar booker-calendar" :class="{ 'calendar--mobile': !isDesktop }">
    <!-- MOBILE SUMMARY -->
    <div v-if="!isDesktop" class="calendar__summary" aria-live="polite">
      <template v-if="mode === 'range'">
        <div class="calendar__summary-item" :class="{ 'calendar__summary-item--active': selecting === 'start' }"
          role="status" :aria-label="localized.arrival">
          <span class="calendar__summary-caption">{{ localized.arrival }}</span>
          <span class="calendar__summary-date">{{ formatMobDate(internalStart) || '---' }}</span>
        </div>
        <div class="calendar__summary-item"
          :class="{ 'calendar__summary-item--active': (internalStart && !internalEnd) || selecting === 'end' }"
          role="status" :aria-label="localized.departure">
          <span class="calendar__summary-caption">{{ localized.departure }}</span>
          <span class="calendar__summary-date">{{ formatMobDate(internalEnd) || '---' }}</span>
        </div>
        <div class="calendar__summary-stats">
          <span class="calendar__summary-caption">{{ props.unit === 'nights' ? (localized.nights_label) :
            (localized.days_label) }}</span>
          <span class="calendar__summary-date">{{ countNights }}</span>
        </div>
      </template>
      <template v-else>
        <div class="calendar__summary-item calendar__summary-item--active" role="status"
          :aria-label="localized.date_label">
          <span class="calendar__summary-caption">{{ localized.date_label }}</span>
          <span class="calendar__summary-date">{{ formatMobDate(internalStart) }}</span>
        </div>
      </template>
    </div>

    <!-- Fixed Weekdays Header for Mobile -->
    <div v-if="!isDesktop" class="calendar__weekdays calendar__weekdays--header" aria-hidden="true">
      <div v-for="lbl in shortDays" :key="lbl" class="calendar__weekday">
        {{ lbl }}
      </div>
    </div>

    <!-- CALENDAR CONTENT -->
    <div ref="scrollArea" class="calendar__layout"
      :class="{ 'calendar__layout--mobile': !isDesktop, 'calendar__layout--ready': isReady || isDesktop }"
      @scroll="onScroll">

      <!-- Desktop Navigation Bar -->
      <nav v-if="isDesktop" class="calendar__nav" :aria-label="localized.calendar_nav">
        <button type="button" class="calendar__nav-btn calendar__nav-btn--prev" @click="moveMonth(-1)"
          :disabled="isPrevOff" :aria-label="localized.prev_month">
          <span class="font-icons icons-angle-left"></span>
        </button>
        <div class="calendar__nav-titles">
          <div v-for="m in visibleMonths" :key="m.id" class="calendar__nav-month">
            {{ m.name }} <span>{{ m.year }}</span>
          </div>
        </div>
        <button type="button" class="calendar__nav-btn calendar__nav-btn--next" @click="moveMonth(1)"
          :aria-label="localized.next_month">
          <span class="font-icons icons-angle-right"></span>
        </button>
      </nav>

      <div class="calendar__body">
        <article v-for="(month, mi) in allMonths" :key="month.id" :id="'month-' + month.id" class="calendar__month"
          :class="{ 'calendar__month--hidden': isDesktop && !isMonthOn(mi) }" ref="monthEls" role="grid"
          :aria-label="month.name + ' ' + month.year">
          <h3 v-if="!isDesktop" class="calendar__month-title">{{ month.name }} {{ month.year }}</h3>

          <div v-if="isDesktop" class="calendar__weekdays" role="row">
            <div v-for="lbl in shortDays" :key="lbl" class="calendar__weekday" role="columnheader" :aria-label="lbl">
              {{ lbl }}
            </div>
          </div>

          <div class="calendar__days">
            <div v-for="b in month.offset" :key="'b-' + month.id + '-' + b" class="calendar__day calendar__day--empty"
              aria-hidden="true"></div>
            <div v-for="day in month.days" :key="day.dateISO" class="calendar__day" :class="calcDayClasses(day)"
              role="gridcell" :aria-label="getDayAriaLabel(day)" :aria-selected="isDaySelected(day)"
              :aria-disabled="day.off" :tabindex="day.off ? -1 : 0" @click="onDayClick(day)"
              @mouseenter="onDayHover(day)" @keydown.enter.prevent="onDayClick(day)"
              @keydown.space.prevent="onDayClick(day)">
              <div v-if="shouldShowTooltip(day)" class="calendar__day-tooltip">
                {{ localized.tooltip_text }}
              </div>
              <div class="calendar__day-val" aria-hidden="true">{{ day.num }}</div>
              <div v-if="prices[day.dateISO]" class="calendar__day-price" aria-hidden="true">
                <template v-if="prices[day.dateISO].available">{{ prices[day.dateISO].avgPrice }}</template>
              </div>
            </div>
          </div>
        </article>
        <!-- SCROLL SPACER -->
        <div v-if="!isDesktop" class="calendar__scroll-spacer" aria-hidden="true"></div>
      </div>
    </div>

    <!-- FOOTER -->
    <footer v-if="!isDesktop || hasPrices" class="calendar__footer">
      <div v-if="hasPrices" class="calendar__legend" role="presentation">
        <div class="calendar__legend-item calendar__legend-item--low">
          <div class="calendar__legend-swatch"></div> {{ localized.low_prices }}
        </div>
        <div class="calendar__legend-item calendar__legend-item--lower">
          <div class="calendar__legend-swatch"></div> {{ localized.lower_prices }}
        </div>
        <div v-if="isDesktop" class="calendar__footer-disclaimer">
          {{ localized.price_disclaimer }}
        </div>
      </div>
      <div v-if="!isDesktop" class="calendar__actions">
        <button type="button" class="btnTertiary" @click="$emit('close')" :aria-label="localized.omit">
          {{ localized.omit }}
        </button>
        <button type="button" class="btnPrimary" @click="onApply" :disabled="!canApply" :aria-label="localized.apply">
          {{ localized.apply }}
        </button>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick, getCurrentInstance } from 'vue';

const props = defineProps({
  modelValue: { type: Object, default: () => ({ start: null, end: null }) },
  mode: { type: String, default: 'range' },
  minDate: { type: [Date, String], default: () => new Date() },
  prices: { type: Object, default: () => ({}) },
  unit: { type: String, default: 'nights' }, // 'nights' or 'days'
  minDays: { type: Number, default: 1 },     // min count required (1-indexed)
  nOffset: { type: Number, default: 0 },     // adjustment for tooltip (e.g. -1 for nights)
  currency: { type: String, default: '' },
  startLabel: { type: String, default: '' },
  endLabel: { type: String, default: '' }
});

const emit = defineEmits(['modelValue', 'select', 'close', 'apply', 'viewDate']);

const { proxy } = getCurrentInstance();
const __ = proxy.__;

const culture = window.__pt?.settings?.site?.culture || 'es-MX';

const viewDate = ref(new Date());
const isDesktop = ref(window.innerWidth >= 768);
const internalStart = ref(null);
const internalEnd = ref(null);
const selecting = ref('start');
const cursor = ref(null);
const scrollArea = ref(null);
const monthEls = ref([]);
const isReady = ref(false);
const emittedMonths = new Set();

onMounted(() => {
  if (isDesktop.value) {
    emitVisible();
  }
});

onUnmounted(() => {
  if (scrollTimer) clearTimeout(scrollTimer);
});
const monthNameFormatter = new Intl.DateTimeFormat(culture, { month: 'long' });
const mobDateFormatter = new Intl.DateTimeFormat(culture, { weekday: 'short', day: 'numeric', month: 'short' });
const dayNameFormatter = new Intl.DateTimeFormat(culture, { weekday: 'short' });

const shortDays = computed(() => {
  const list = [];
  const base = new Date(2024, 0, 1);
  for (let i = 0; i < 7; i++) {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    list.push(cap(dayNameFormatter.format(d).replace(/\./g, '')));
  }
  return list;
});

function toLocalDate(val) {
  if (!val) return null;
  if (val instanceof Date) return new Date(val.getFullYear(), val.getMonth(), val.getDate());

  if (typeof val === 'string') {
    const parts = val.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (parts) {
      return new Date(parseInt(parts[1]), parseInt(parts[2]) - 1, parseInt(parts[3]));
    }
    const d = new Date(val);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  return null;
}


function safeEmitViewDate(date) {
  if (!date) return;
  const monthId = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  if (emittedMonths.has(monthId)) return;

  emittedMonths.add(monthId);
  emit('viewDate', date);
}

function emitVisible() {
  if (isDesktop.value) {
    visibleMonths.value.forEach(m => safeEmitViewDate(m.date));
  }
}

function scrollToMonth(date) {
  if (!date || isDesktop.value) return;
  const monthId = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  const el = document.getElementById('month-' + monthId);
  if (el && scrollArea.value) {
    el.scrollIntoView({ behavior: 'auto', block: 'start' });
  }
}

let scrollTimer = null;
function onScroll(immediate = false) {
  if (isDesktop.value || !scrollArea.value) return;
  if (scrollTimer) clearTimeout(scrollTimer);

  const detect = () => {
    const rectCont = scrollArea.value.getBoundingClientRect();
    const articles = scrollArea.value.querySelectorAll('.calendar__month');

    articles.forEach(el => {
      const rect = el.getBoundingClientRect();
      const isVisible = rect.top < rectCont.bottom - 50 && rect.bottom > rectCont.top + 50;

      if (isVisible) {
        const monthId = el.id.replace('month-', '');
        const [y, m] = monthId.split('-').map(Number);
        safeEmitViewDate(new Date(y, m - 1, 1));
      }
    });
  };

  if (immediate) {
    detect();
  } else {
    scrollTimer = setTimeout(detect, 100);
  }
}


const hasPrices = computed(() => Object.keys(props.prices).length > 0);
const isPrevOff = computed(() => {
  const minD = toLocalDate(props.minDate) || new Date();
  return viewDate.value.getFullYear() <= minD.getFullYear() && viewDate.value.getMonth() <= minD.getMonth();
});

const canApply = computed(() => {
  if (props.mode === 'single') return !!internalStart.value;
  if (!internalStart.value || !internalEnd.value) return false;
  const diff = Math.abs(internalEnd.value.getTime() - internalStart.value.getTime());
  const count = Math.round(diff / (1000 * 3600 * 24)) + 1;
  
  return count >= props.minDays;
});

const countNights = computed(() => {
  if (!internalStart.value || !internalEnd.value) return 0;
  const diff = Math.abs(internalEnd.value.getTime() - internalStart.value.getTime());
  const n = Math.round(diff / (1000 * 3600 * 24));
  return (n + 1) + props.nOffset; // Use offset to adjust total days (n + 1)
});

const nightsPreview = computed(() => {
  if (!internalStart.value) return 0;
  const en = internalEnd.value ? internalEnd.value.getTime() : cursor.value;
  if (!en) return 0;
  const diff = Math.abs(en - internalStart.value.getTime());
  const n = Math.round(diff / (1000 * 3600 * 24));
  return (n + 1) + props.nOffset; // Use offset to adjust total days (n + 1)
});

const localized = computed(() => ({
  arrival: props.startLabel || __('booker.calendar_arrival'),
  departure: props.endLabel || __('booker.calendar_departure'),
  date_label: __('booker.calendar_date'),
  omit: __('booker.calendar_omit'),
  apply: __('booker.apply'),
  nights: __('booker.nights'),
  nights_label: `${countNights.value === 1 ? __('booker.calendar_night') : __('booker.calendar_nights')}`,
  days_label: `${countNights.value === 1 ? __('booker.calendar_day') : __('booker.calendar_days')}`,
  tooltip_text: props.unit === 'days'
    ? `${nightsPreview.value} ${nightsPreview.value === 1 ? __('booker.calendar_day') : __('booker.calendar_days')}`
    : `${nightsPreview.value} ${nightsPreview.value === 1 ? __('booker.calendar_night') : __('booker.calendar_nights')}`,
  next_month: __('booker.next_month'),
  prev_month: __('booker.prev_month'),
  unavailable: __('booker.unavailable'),
  start_date: __('booker.start_date'),
  end_date: __('booker.end_date'),
  low_prices: __('booker.low_prices'),
  lower_prices: __('booker.lower_prices'),
  calendar_nav: __('booker.calendar_nav'),
  price_disclaimer: __('booker.price_disclaimer', [props.currency, props.unit === 'days' ? __('booker.calendar_day') : __('booker.calendar_night')])
}));

const allMonths = computed(() => {
  const list = [];
  const minD = toLocalDate(props.minDate) || new Date();
  const startM = new Date(minD.getFullYear(), minD.getMonth(), 1);

  for (let i = 0; i < 24; i++) { // Show up to 24 months
    const mDate = new Date(startM.getFullYear(), startM.getMonth() + i, 1);
    const y = mDate.getFullYear();
    const m = mDate.getMonth();
    list.push({
      id: `${y}-${String(m + 1).padStart(2, '0')}`,
      name: cap(monthNameFormatter.format(mDate)),
      year: y,
      offset: (mDate.getDay() + 6) % 7,
      days: getMonthDays(y, m),
      date: mDate
    });
  }
  return list;
});

function getMonthDays(y, m) {
  const arr = [];
  const count = new Date(y, m + 1, 0).getDate();
  const minLimit = toLocalDate(props.minDate) || toLocalDate(new Date());

  for (let i = 1; i <= count; i++) {
    const d = new Date(y, m, i);
    const iso = `${y}-${String(m + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
    arr.push({
      num: i,
      dateISO: iso,
      off: d.getTime() < minLimit.getTime(),
      date: d
    });
  }
  return arr;
}

const visibleMonths = computed(() => {
  if (!isDesktop.value) return allMonths.value;
  const curY = viewDate.value.getFullYear();
  const curM = viewDate.value.getMonth();
  const idx = allMonths.value.findIndex(m => m.date.getFullYear() === curY && m.date.getMonth() === curM);
  return allMonths.value.slice(idx === -1 ? 0 : idx, idx === -1 ? 2 : idx + 2);
});

function isMonthOn(idx) {
  if (!isDesktop.value) return true;
  const curY = viewDate.value.getFullYear();
  const curM = viewDate.value.getMonth();
  const targetIdx = allMonths.value.findIndex(m => m.date.getFullYear() === curY && m.date.getMonth() === curM);
  return idx === targetIdx || idx === targetIdx + 1;
}

function onDayClick(day) {
  if (day.off) return;
  const d = new Date(day.date);

  if (props.mode === 'single') {
    const s = toLocalDate(props.modelValue.start);
    const e = toLocalDate(props.modelValue.end);
    let diff = 7 * 24 * 3600 * 1000; // Default 7 days
    if (s && e) {
      diff = Math.abs(e.getTime() - s.getTime());
    }
    internalStart.value = d;
    internalEnd.value = new Date(d.getTime() + diff);
    notify();
    if (isDesktop.value) {
      emit('select', { start: internalStart.value, end: internalEnd.value });
      emit('close');
    }
  } else {
    if (!internalStart.value || (internalStart.value && internalEnd.value)) {
      internalStart.value = d; internalEnd.value = null; selecting.value = 'end';
    } else {
      const stTs = internalStart.value.getTime();
      const enTs = d.getTime();
      const low = Math.min(stTs, enTs);
      const high = Math.max(stTs, enTs);
      const diff = Math.abs(high - low);
      const n = Math.round(diff / (1000 * 3600 * 24));
      const count = n + 1; // Pure selection count (total days) for validation

      if (count < props.minDays) {
        if (stTs !== enTs) {
          internalStart.value = d; internalEnd.value = null; selecting.value = 'end';
        }
        return;
      }

      internalStart.value = new Date(low);
      internalEnd.value = new Date(high);
      selecting.value = 'start';
      if (isDesktop.value) emit('select', { start: internalStart.value, end: internalEnd.value });
    }
  }
  if (isDesktop.value) notify();
}

function notify() {
  let s = internalStart.value;
  let e = internalEnd.value;
  if (s && !e) {
    const nextDate = new Date(s);
    nextDate.setDate(nextDate.getDate() + 7);
    e = nextDate;
  }
  emit('modelValue', { start: s, end: e });
}
function onDayHover(day) { if (!day.off) cursor.value = day.date.getTime(); }

function calcDayClasses(day) {
  const st = internalStart.value ? internalStart.value.getTime() : null;
  const en = internalEnd.value ? internalEnd.value.getTime() : null;
  const cur = day.date.getTime();
  const isRange = props.mode === 'range';

  const p = props.prices[day.dateISO];
  const obj = {
    'calendar__day--disabled': day.off,
    'calendar__day--start': false,
    'calendar__day--end': false,
    'calendar__day--in-range': false,
    'calendar__day--is-range': isRange
  };

  if (p && p.color) obj[`calendar__day--${p.color}`] = true;

  if (st && en && isRange) {
    obj['calendar__day--start'] = st === cur;
    obj['calendar__day--end'] = en === cur;
    obj['calendar__day--in-range'] = cur >= st && cur <= en;
  } else if (st && cursor.value && isRange) {
    const low = Math.min(st, cursor.value);
    const high = Math.max(st, cursor.value);
    obj['calendar__day--start'] = cur === low;
    obj['calendar__day--end'] = cur === high;
    obj['calendar__day--in-range'] = cur >= low && cur <= high;
  } else {
    obj['calendar__day--start'] = st === cur;
  }

  if (shouldShowTooltip(day)) {
    obj['calendar__day--has-tooltip'] = true;
  }

  return obj;
}

function shouldShowTooltip(day) {
  if (!isDesktop.value || props.mode !== 'range' || !internalStart.value || internalEnd.value) return false;
  const cur = day.date.getTime();
  const st = internalStart.value.getTime();
  const en = internalEnd.value ? internalEnd.value.getTime() : cursor.value;

  if (!en) return false;
  const diff = Math.abs(en - st);
  const n = Math.round(diff / (1000 * 3600 * 24));
  const count = n + 1; // Pure selection count (total days) for validation
  if (count < props.minDays) return false;

  return cur === en;
}

const fullDateFormatter = new Intl.DateTimeFormat(culture, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

function getDayAriaLabel(day) {
  if (day.off) return `${fullDateFormatter.format(day.date)}, ${localized.value.unavailable}`;
  let label = fullDateFormatter.format(day.date);
  if (internalStart.value && internalStart.value.getTime() === day.date.getTime()) label += `, ${localized.value.start_date}`;
  if (internalEnd.value && internalEnd.value.getTime() === day.date.getTime()) label += `, ${localized.value.end_date}`;
  if (props.prices && props.prices[day.dateISO]?.available) {
    label += `, ${props.prices[day.dateISO].avgPrice}`;
  }
  return label;
}

function isDaySelected(day) {
  const st = internalStart.value ? internalStart.value.getTime() : null;
  const en = internalEnd.value ? internalEnd.value.getTime() : null;
  const cur = day.date.getTime();
  if (st && en && props.mode === 'range') return cur >= st && cur <= en;
  return st === cur;
}

function moveMonth(n) {
  const d = new Date(viewDate.value);
  d.setMonth(d.getMonth() + n);
  viewDate.value = d;
  emitVisible();
}

function onApply() {
  notify();
  const payload = { start: internalStart.value, end: internalEnd.value };
  emit('select', payload);
  emit('close');
}

function formatMobDate(d) { return d ? cap(mobDateFormatter.format(d)) : null; }
function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; }

let observer = null;
function setupObserver() {
  if (isDesktop.value || !scrollArea.value) return;
  const options = { root: scrollArea.value, threshold: 0.1, rootMargin: '0px' };

  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const monthId = entry.target.id.replace('month-', '');
        const [y, m] = monthId.split('-').map(Number);
        const mDate = new Date(y, m - 1, 1);
        safeEmitViewDate(mDate);
      }
    });
  }, options);

  nextTick(() => {
    monthEls.value.forEach(el => { if (el) observer.observe(el); });
  });
}

const resize = () => { isDesktop.value = window.innerWidth >= 768; };
onMounted(() => {
  window.addEventListener('resize', resize);
  setupObserver();
  if (isDesktop.value) {
    emitVisible();
  }
  safeEmitViewDate(viewDate.value);
});

onUnmounted(() => {
  window.removeEventListener('resize', resize);
  if (observer) observer.disconnect();
  if (scrollTimer) clearTimeout(scrollTimer);
  emittedMonths.clear();
});

watch(() => props.modelValue, (v) => {
  let s = toLocalDate(v?.start);
  let e = toLocalDate(v?.end);

  if (s && !e) {
    const nextDate = new Date(s);
    nextDate.setDate(nextDate.getDate() + 7);
    e = nextDate;
    emit('modelValue', { start: s, end: e });
  }

  const oldStart = internalStart.value;
  internalStart.value = s;
  internalEnd.value = e;

  if (s && (!oldStart || s.getTime() !== oldStart.getTime())) {
    viewDate.value = new Date(s.getFullYear(), s.getMonth(), 1);
    emitVisible();

    isReady.value = false;
    nextTick(() => {
      scrollToMonth(s);
      setTimeout(() => {
        onScroll(true);
        isReady.value = true;
      }, 100);
    });
  } else if (!s) {
    isReady.value = true;
  }
}, { deep: true, immediate: true });
</script>

<style lang="scss" scoped>
.calendar,
.booker-calendar {
  background: var(--bg-base);
  color: var(--text-strong);
  user-select: none;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;

  &:not(.calendar--mobile) {
    padding: var(--space-12);
    border-radius: var(--space-16);
    width: 720px;
    height: auto;

    @media (min-width: 992px) {
      width: 750px;
    }
  }

  &--mobile {
    height: 100%;
    overflow: hidden;

    :deep(.smartbox__content) {
      padding: 0 !important;
      overflow: hidden !important;
    }
  }

  &__summary {
    display: flex;
    gap: var(--space-8);
    padding-bottom: var(--space-16);
    align-items: center;
    background: var(--bg-base);
    flex-shrink: 0;
  }

  &__summary-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    border-bottom: 2px solid transparent;

    &--active {
      border-color: transparent;
    }
  }

  &__summary-caption {
    font-size: 0.625rem;
    color: var(--text-subtle);
    text-transform: uppercase;
  }

  &__summary-date {
    font-size: 0.875rem;
    font-weight: 700;
    height: 18px;
    line-height: 18px;
    color: var(--text-strong);
  }

  &__summary-stats {
    text-align: right;

    .calendar__summary-date {
      font-size: 1.125rem;
    }
  }

  &__nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--space-8);
    padding: 0 var(--space-12);
    flex-shrink: 0;
  }

  &__nav-titles {
    display: flex;
    flex: 1;
    justify-content: space-around;
    font-size: 0.9375rem;
  }

  &__nav-month {
    font-weight: 700;
    color: var(--text-strong);
    text-transform: capitalize;

    span {
      color: var(--text-subtle);
      font-weight: 400;
      margin-left: var(--space-4);
    }
  }

  &__nav-btn {
    background: var(--bg-base);
    border: 1px solid var(--border-subtle);
    width: 34px;
    height: 34px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    &:hover {
      background: var(--bg-level3);
      box-shadow: var(--shadow-300);
    }

    &:disabled {
      opacity: 0.1;
      cursor: default;
    }
  }

  &__layout {
    flex: 1;
    overflow-y: auto;
    background: var(--bg-base);
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    -ms-overflow-style: none;

    &--mobile {
      padding-bottom: 0px;
      opacity: 0;
      transition: opacity 0.1s;
    }

    &--ready {
      opacity: 1 !important;
    }

    &:not(&--mobile) {
      max-height: 420px;
      overflow: visible;
    }
  }

  &__body {
    display: flex;
    flex-wrap: wrap;
    gap: 0px;

    @media (min-width: 768px) {
      flex-wrap: nowrap;
      gap: var(--space-28);
      justify-content: space-between;
      padding: 0;
    }
  }

  &__month {
    flex: 1 1 100%;
    margin: 0 auto;
    padding-bottom: var(--space-24);

    @media (min-width: 768px) {
      padding-bottom: var(--space-4);
    }

    &--hidden {
      display: none;
    }

    &:first-of-type {
      padding-top: var(--space-16);

      @media (min-width: 768px) {
        padding-top: 0px;
      }
    }
  }

  &__month-title {
    font-size: 1.125rem;
    font-weight: 700;
    margin-bottom: var(--space-16);
  }

  &__weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    text-align: center;
    padding-bottom: var(--space-12);
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--text-strong);

    &--header {
      background: var(--bg-base, white);
      padding-top: var(--space-8);
      padding-bottom: var(--space-8);
      width: 100%;
      box-sizing: border-box;
      border-bottom: 2px solid var(--bg-level-1, #f4f5f7);
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      column-gap: 2px;

    }
  }

  &__days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    row-gap: 2px;
    column-gap: 2px;
  }

  &__day {
    height: 46px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;
    // z-index removed to allow negative z-index children to sit behind background/border
    font-size: 0.875rem;
    font-weight: 500;
    transition: background 0.2s;
    border-radius: var(--space-4);

    &-tooltip {
      position: absolute;
      top: -42px;
      left: 50%;
      transform: translateX(-50%);
      background: var(--bg-base) !important;
      color: var(--text-strong) !important;
      padding: 6px 12px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 700;
      white-space: nowrap;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.25);
      z-index: 100;
      pointer-events: none;
      display: block;

      &::after {
        content: '';
        position: absolute;
        bottom: -6px;
        left: 50%;
        transform: translateX(-50%) rotate(45deg);
        width: 10px;
        height: 10px;
        background: #fff;
        box-shadow: 2px 2px 2px rgba(0, 0, 0, 0.05);
      }
    }

    &--disabled,
    &--empty {
      color: var(--text-disabled) !important;
      cursor: default;
    }

    &--bg-success {
      background-color: #bce3b2;
      color: var(--text-strong);
    }

    &--bg-success-light {
      background-color: #e7ffdd;
      color: var(--text-strong);
    }

    &--has-tooltip {
      z-index: 50;
    }

    &--in-range {
      background-color: var(--bg-primary-level1);
      border-radius: 0;
      position: relative;

      &.calendar__day--start {
        border-radius: var(--space-4) 0 0 var(--space-4);
      }

      &.calendar__day--end {
        border-radius: 0 var(--space-4) var(--space-4) 0;
      }

      &.calendar__day--start.calendar__day--end {
        border-radius: var(--space-4);
      }
    }

    &--start,
    &--end {
      background-color: var(--bg-primary);
      color: var(--bg-base);
      font-weight: 700;
      z-index: 2;
      border-radius: var(--space-4);

      &.calendar__day--is-range {
        &.calendar__day--start {
          border-radius: var(--space-4) 0 0 var(--space-4);
        }

        &.calendar__day--end {
          border-radius: 0 var(--space-4) var(--space-4) 0;
        }

        &.calendar__day--start.calendar__day--end {
          border-radius: var(--space-4);
        }
      }

      .calendar__day-price {
        color: rgba(255, 255, 255, 0.8);
      }
    }

    &--is-range.calendar__day--start::after {
      content: "";
      position: absolute;
      right: -8px;
      top: 50%;
      transform: translateY(-50%);
      border-top: 8px solid transparent;
      border-bottom: 8px solid transparent;
      border-left: 8px solid var(--bg-primary);
      z-index: 3;
    }

    &--is-range.calendar__day--end::before {
      content: "";
      position: absolute;
      left: -8px;
      top: 50%;
      transform: translateY(-50%);
      border-top: 8px solid transparent;
      border-bottom: 8px solid transparent;
      border-right: 8px solid var(--bg-primary);
      z-index: 3;
    }

    &--start.calendar__day--end::after,
    &--start.calendar__day--end::before {
      display: none;
    }

    @media (min-width: 768px) {
      &:hover:not(&--disabled):not(&--empty):not(&--in-range) {
        border: 1px solid var(--border-primary);
        z-index: 10;
      }
    }
  }

  &__day-val {
    font-size: 0.9375rem;
    font-weight: 700;
    margin-bottom: 0px;
    z-index: 2;
    pointer-events: none;
    line-height: normal;
  }

  &__day-price {
    font-size: 0.625rem;
    color: var(--text-subtle);
    pointer-events: none;
    min-height: 12px;
    line-height: 1;
    margin-top: 1px;
  }

  &__footer {
    background: var(--bg-base);
    padding: 0;
    border-top: 1px solid var(--border-subtle);
    flex-shrink: 0;
  }

  &__legend {
    display: flex;
    column-gap: var(--space-24);
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    font-size: 0.75rem;
    color: var(--text-subtle);
    padding-top: var(--space-16);
  }

  &__legend-item {
    display: flex;
    align-items: center;
    gap: var(--space-8);
  }

  &__legend-swatch {
    width: 22px;
    height: 22px;
    border-radius: var(--space-4);
    border: 1px solid var(--border-subtle);
  }

  &__legend-item--low .calendar__legend-swatch {
    background: #E7FFDD;
  }

  &__legend-item--lower .calendar__legend-swatch {
    background: #BCE3B2;
  }

  &__footer-disclaimer {
    text-align: center;
  }

  &__actions {
    position: fixed;
    bottom: 0px;
    left: 0px;
    display: flex;
    align-items: center;
    width: 100%;
    gap: var(--space-16);
    padding: var(--space-16);
    background-color: var(--bg-base);
    border-top: 1px solid var(--border-subtle);
    z-index: 2;

    .btnTertiary,
    .btnPrimary {
      flex: 1;
      white-space: nowrap;
    }
  }

  &__scroll-spacer {
    height: 40px;
    width: 100%;
    flex-shrink: 0;
  }
}
</style>
